import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Input,
  Flex,
  Button,
  HStack,
  IconButton,
  Text,
  useColorModeValue,
  VStack,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import axios from "axios";
import { API_ENDPOINTS } from "../api/api.js";
import {
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Search,
} from "lucide-react";

export default function VishalContacts() {
  // 🔹 Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  // 🔹 Contact state
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [page, setPage] = useState(1);
  const pageSize = 5;

  // 🔹 Check localStorage for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      setIsAuthenticated(true);
    }
  }, []);

  // 🔹 Handle login with backend
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(API_ENDPOINTS.auth.login, {
        username: loginId,
        password: password,
      });

      if (res.status === 200 && res.data.success) {
        const token = res.data.token;
        localStorage.setItem("authToken", token); // ✅ save token
        setIsAuthenticated(true);
        setLoginError("");
      } else {
        setLoginError(res.data.message || "Login failed.");
      }
    } catch (err) {
      setLoginError(err.response?.data?.message || "Invalid ID or password.");
    }
  };

  // 🔹 Fetch contacts when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const fetchContacts = async () => {
        setLoading(true);
        try {
          const token = localStorage.getItem("authToken");
          const res = await axios.get(API_ENDPOINTS.pentagonContact.getAll, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setContacts(res.data.data || []);
        } catch (err) {
          console.error("Error fetching contacts:", err);
        } finally {
          setLoading(false);
        }
      };
      fetchContacts();
    }
  }, [isAuthenticated]);

  // Filtering
  const filteredContacts = contacts.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase()) ||
      c.message.toLowerCase().includes(search.toLowerCase())
  );

  // Sorting
  const sortedContacts = [...filteredContacts].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  });

  // Pagination
  const startIndex = (page - 1) * pageSize;
  const paginatedContacts = sortedContacts.slice(
    startIndex,
    startIndex + pageSize
  );
  const totalPages = Math.ceil(sortedContacts.length / pageSize);

  // Handle Sort
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Export Excel
  const handleExport = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.get(API_ENDPOINTS.pentagonContact.export, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "pentagon_contacts.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Excel export failed:", err);
    }
  };

  const bgHeader = useColorModeValue("gray.100", "gray.700");

  // 🔹 Show login if not authenticated
  if (!isAuthenticated) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="gray.100">
        <Box
          bg="white"
          p={8}
          rounded="xl"
          shadow="lg"
          w="100%"
          maxW="400px"
          textAlign="center"
        >
          <Heading mb={6} size="lg">
            🔒 Secure Access
          </Heading>
          <form onSubmit={handleLogin}>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Username</FormLabel>
                <Input
                  type="text"
                  value={loginId}
                  onChange={(e) => setLoginId(e.target.value)}
                  placeholder="Enter username"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Password</FormLabel>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                />
              </FormControl>
              {loginError && (
                <Text color="red.500" fontSize="sm">
                  {loginError}
                </Text>
              )}
              <Button
                type="submit"
                colorScheme="blue"
                w="full"
                borderRadius="full"
              >
                Login
              </Button>
            </VStack>
          </form>
        </Box>
      </Flex>
    );
  }

  // 🔹 Show table if authenticated
  return (
    <Box p={8} bg={useColorModeValue("gray.50", "gray.900")} minH="100vh">
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg" fontWeight="600">
          Pentagon Contacts Us
        </Heading>
        <Button
          leftIcon={<Download size={18} />}
          colorScheme="green"
          onClick={handleExport}
        >
          Export Excel
        </Button>
      </Flex>

      {/* Search bar */}
      <Flex mb={4} justify="flex-end" align="center" gap={2}>
        <Search size={18} />
        <Input
          placeholder="Search contacts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          maxW="300px"
          borderRadius="lg"
        />
      </Flex>

      {loading ? (
        <Flex justify="center" align="center" minH="200px">
          <Spinner size="xl" color="blue.500" />
        </Flex>
      ) : (
        <>
          <Table variant="simple" size="md">
            <Thead bg={bgHeader}>
              <Tr>
                <Th cursor="pointer" onClick={() => handleSort("name")}>
                  Name{" "}
                  {sortConfig.key === "name" &&
                    (sortConfig.direction === "asc" ? (
                      <ArrowUp size={16} />
                    ) : (
                      <ArrowDown size={16} />
                    ))}
                </Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <Th>Message</Th>
                <Th>UTM</Th>
                <Th>Landing Page</Th>
                <Th>Device</Th>
                <Th>Timezone</Th>
                <Th cursor="pointer" onClick={() => handleSort("createdAt")}>
                  Date{" "}
                  {sortConfig.key === "createdAt" &&
                    (sortConfig.direction === "asc" ? (
                      <ArrowUp size={16} />
                    ) : (
                      <ArrowDown size={16} />
                    ))}
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {paginatedContacts.map((c) => (
                <Tr key={c._id}>
                  <Td fontWeight="500">{c.name}</Td>
                  <Td>{c.email}</Td>
                  <Td>{c.phone || "-"}</Td>
                  <Td maxW="200px" whiteSpace="normal">
                    {c.message}
                  </Td>
                  <Td fontSize="sm" whiteSpace="normal">
                    <b>Src:</b> {c.utms?.utm_source || "-"} <br />
                    <b>Med:</b> {c.utms?.utm_medium || "-"} <br />
                    <b>Camp:</b> {c.utms?.utm_campaign || "-"}
                  </Td>
                  <Td maxW="250px" whiteSpace="normal">
                    {c.landing_page}
                  </Td>
                  <Td>{c.device_type || "-"}</Td>
                  <Td>{c.timezone || "-"}</Td>
                  <Td>{new Date(c.createdAt).toLocaleString()}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>

          {/* Pagination controls */}
          <HStack justify="center" mt={6} spacing={4}>
            <IconButton
              icon={<ChevronLeft />}
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              isDisabled={page === 1}
              aria-label="Previous Page"
            />
            <Text>
              Page <b>{page}</b> of <b>{totalPages}</b>
            </Text>
            <IconButton
              icon={<ChevronRight />}
              onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              isDisabled={page === totalPages}
              aria-label="Next Page"
            />
          </HStack>
        </>
      )}
    </Box>
  );
}
